import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FiEye, FiEyeOff } from "react-icons/fi";
import { FcGoogle } from "react-icons/fc";
import "../styles/AuthStyles.css";
import { useAuth } from "../context/AuthContext";
import { sendPasswordResetEmail } from "firebase/auth";
import { auth } from "../firebase";
import authBg from "../assets/auth-bg-v2.png";
import logo from "../assets/orvion-logo.png";
import CustomModal from "../components/CustomModal";

function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [credentials, setCredentials] = useState({
    email: "",
    password: "",
    role: "freelancer",
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [modalState, setModalState] = useState({
    isOpen: false,
    title: "",
    message: "",
    type: "info",
    onConfirm: null
  });

  const handleChange = (e) => {
    setCredentials({ ...credentials, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const data = await login(credentials.email, credentials.password);

      if (data.user) {
        setModalState({
          isOpen: true,
          title: "Success",
          message: "Logged in successfully!",
          type: "success",
          onConfirm: () => {
            setModalState(prev => ({ ...prev, isOpen: false }));
            if (data.user.role === 'admin') {
              navigate("/admin/dashboard");
            } else {
              navigate("/dashboard");
            }
          }
        });
      } else {
        setModalState({
          isOpen: true,
          title: "Login Failed",
          message: data.msg || "Failed to login",
          type: "error",
          onConfirm: () => setModalState(prev => ({ ...prev, isOpen: false }))
        });
      }
    } catch (err) {
      setModalState({
        isOpen: true,
        title: "Error",
        message: err.message,
        type: "error",
        onConfirm: () => setModalState(prev => ({ ...prev, isOpen: false }))
      });
    }

    setLoading(false);
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    if (!resetEmail) {
      setModalState({
        isOpen: true,
        title: "Input Required",
        message: "Please enter your email address",
        type: "info",
        onConfirm: () => setModalState(prev => ({ ...prev, isOpen: false }))
      });
      return;
    }

    try {
      await sendPasswordResetEmail(auth, resetEmail);
      setModalState({
        isOpen: true,
        title: "Email Sent",
        message: "Password reset email sent! Please check your inbox and SPAM folder.",
        type: "success",
        onConfirm: () => {
          setModalState(prev => ({ ...prev, isOpen: false }));
          setShowResetPassword(false);
          setResetEmail("");
        }
      });
    } catch (error) {
      console.error("Password reset error:", error);
      setModalState({
        isOpen: true,
        title: "Error",
        message: `Failed to send reset email: ${error.message}`,
        type: "error",
        onConfirm: () => setModalState(prev => ({ ...prev, isOpen: false }))
      });
    }
  };

  if (showResetPassword) {
    return (
      <div className="orvion-auth-container">
        {/* Left Side - Visual */}
        <div className="orvion-auth-visual">
            <img src={authBg} alt="Background" className="orvion-auth-image-bg" />
            <div className="orvion-auth-overlay"></div>
            <Link to="/" className="orvion-back-link">
                ← Back to landing
            </Link>
        </div>

        {/* Right Side - Form Card */}
        <div className="orvion-auth-card">
            <div className="orvion-form-wrapper">
                <div className="orvion-logo-container">
                    <img src={logo} alt="Orvion Logo" className="orvion-header-logo" />
                </div>

                <div className="orvion-auth-header">
                    <h2 className="orvion-auth-title">Reset Password</h2>
                    <p className="orvion-auth-subtitle">Enter your email to receive a reset link</p>
                </div>

                <div style={{
                    backgroundColor: '#fff7e6',
                    border: '1px solid #ffd591',
                    borderRadius: '8px',
                    padding: '12px',
                    marginBottom: '20px',
                    fontSize: '0.9rem',
                    color: '#d46b08',
                    width: '100%',
                    textAlign: 'center'
                }}>
                    <strong>Note:</strong> Check your <strong>Spam</strong> or <strong>Junk</strong> folder if you don't see the email.
                </div>

                <form onSubmit={handlePasswordReset} className="orvion-auth-form">
                    <div className="orvion-form-group">
                        <label>Email Address</label>
                        <input
                            type="email"
                            value={resetEmail}
                            onChange={(e) => setResetEmail(e.target.value)}
                            placeholder="Enter your email"
                            required
                        />
                    </div>
                    <button type="submit" className="orvion-auth-btn">
                        Send Reset Link
                    </button>
                </form>

                <div className="orvion-auth-footer">
                    <button
                        onClick={() => setShowResetPassword(false)}
                        style={{
                            background: 'none',
                            border: 'none',
                            color: '#6C63FF',
                            cursor: 'pointer',
                            fontSize: '0.9rem',
                            fontWeight: '600',
                            padding: 0
                        }}
                    >
                        ← Back to Login
                    </button>
                </div>
            </div>
        </div>
        <CustomModal
          isOpen={modalState.isOpen}
          onClose={() => setModalState(prev => ({ ...prev, isOpen: false }))}
          title={modalState.title}
          message={modalState.message}
          type={modalState.type}
          onConfirm={modalState.onConfirm}
        />
      </div>
    );
  }

  return (
    <div className="orvion-auth-container">
      {/* Left Side - Visual */}
      <div className="orvion-auth-visual">
        <img src={authBg} alt="Background" className="orvion-auth-image-bg" />
        <div className="orvion-auth-overlay"></div>
        <Link to="/" className="orvion-back-link">
            ← Back to landing
        </Link>
      </div>

      {/* Right Side - Form Card */}
      <div className="orvion-auth-card">
        <div className="orvion-form-wrapper">
            <div className="orvion-logo-container">
                <img src={logo} alt="Orvion Logo" className="orvion-header-logo" />
            </div>

            <div className="orvion-auth-header">
                <h2 className="orvion-auth-title">Welcome Back</h2>
                <p className="orvion-auth-subtitle">Please enter your details to sign in.</p>
            </div>

            {/* Google Button (Coming Soon) */}
            <div className="orvion-google-btn-container">
                <button className="orvion-google-btn" type="button" onClick={() => {}}>
                    <FcGoogle size={22} />
                    <span className="google-btn-text">Sign in with Google</span>
                </button>
            </div>

            <div className="orvion-divider">Or</div>

            <form onSubmit={handleSubmit} className="orvion-auth-form">
                <div className="orvion-form-group">
                    <label>Email Address</label>
                    <input
                        type="email"
                        name="email"
                        placeholder="Enter your email"
                        value={credentials.email}
                        onChange={handleChange}
                        required
                    />
                </div>

                <div className="orvion-form-group">
                    <label>Password</label>
                    <div className="orvion-password-field">
                        <input
                            type={showPassword ? "text" : "password"}
                            name="password"
                            placeholder="Enter your password"
                            value={credentials.password}
                            onChange={handleChange}
                            required
                        />
                        <button
                            type="button"
                            className="orvion-password-toggle"
                            onClick={() => setShowPassword((s) => !s)}
                        >
                            {showPassword ? <FiEyeOff /> : <FiEye />}
                        </button>
                    </div>
                </div>

                <div className="orvion-role-selection">
                    <div
                        className={`orvion-role-toggle ${credentials.role === "admin" ? "client-active" : ""}`}
                        onClick={() => setCredentials({ ...credentials, role: credentials.role === "freelancer" ? "admin" : "freelancer" })}
                    >
                        <div className={`orvion-role-slider`}></div>
                        <div
                            className={`orvion-role-option ${credentials.role === "freelancer" ? "active" : ""}`}
                            onClick={(e) => { e.stopPropagation(); setCredentials({ ...credentials, role: "freelancer" }); }}
                        >
                            Partner
                        </div>
                        <div
                            className={`orvion-role-option ${credentials.role === "admin" ? "active" : ""}`}
                            onClick={(e) => { e.stopPropagation(); setCredentials({ ...credentials, role: "admin" }); }}
                        >
                            Client
                        </div>
                    </div>
                </div>

                <button type="submit" className="orvion-auth-btn" disabled={loading}>
                    {loading ? <div className="loading-spinner"></div> : "Sign In"}
                </button>
            </form>

            <div className="orvion-auth-footer" style={{ display: 'flex', justifyContent: 'space-between', width: '100%', marginTop: '1rem' }}>
                <button
                    onClick={() => setShowResetPassword(true)}
                    style={{
                        background: 'none',
                        border: 'none',
                        color: '#666',
                        cursor: 'pointer',
                        fontSize: '0.85rem',
                        fontWeight: '500',
                        padding: 0
                    }}
                >
                    Forgot Password?
                </button>
                <Link to="/signup" style={{ fontSize: '0.85rem' }}>Sign up</Link>
            </div>
        </div>
      </div>

      <CustomModal
        isOpen={modalState.isOpen}
        onClose={() => setModalState(prev => ({ ...prev, isOpen: false }))}
        title={modalState.title}
        message={modalState.message}
        type={modalState.type}
        onConfirm={modalState.onConfirm}
      />
    </div>
  );
}

export default Login;
